import { motion } from 'framer-motion';
import { getTodayCO } from '@/lib/dateUtils';
import { Clock, Timer, PieChart } from 'lucide-react';
import AdminSidebar from '@/pages/admin/components/AdminSidebar';
import AdminTopBar from '@/pages/admin/components/AdminTopBar';
import StatCard from '@/pages/admin/components/StatCard';
import RecentAppointments from '@/pages/admin/components/RecentAppointments';
import PageHeader from '@/components/feature/PageHeader';
import { useDashboardData } from '@/hooks/useDashboardData';
import { theme } from '@/lib/theme';
import TopCustomersWidget from '@/pages/admin/dashboard/components/TopCustomersWidget';
import DormantCustomersWidget from '@/pages/admin/dashboard/components/DormantCustomersWidget';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.38, ease: 'easeOut' } },
};

export default function AdminDashboard() {
  const { stats, appointments, topCustomers, dormantCustomers, loading, error, refetch } = useDashboardData();

  const today = getTodayCO();
  const todayAppointments = appointments
    .filter((a) => a.appointment_date === today)
    .sort((a, b) => a.start_time.localeCompare(b.start_time))
    .slice(0, 5);

  const confirmed = appointments.filter((a) => a.status === 'confirmed').length;
  const pending = appointments.filter((a) => a.status === 'pending').length;
  const cancelled = appointments.filter((a) => a.status === 'cancelled').length;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: theme.colors.bgBase }}>
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar
          title="Dashboard"
          subtitle={new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
        />

        <main className="flex-1 overflow-y-auto px-6 py-8">
          <PageHeader
            title="Bienvenido de vuelta"
            subtitle="Aquí tienes el resumen de hoy"
            action={{
              label: 'Actualizar',
              icon: 'ri-refresh-line',
              onClick: refetch,
              loading,
            }}
          />

          {/* Error banner */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-xl px-5 py-4 mb-6 border"
              style={{
                backgroundColor: theme.colors.dangerMuted,
                borderColor: 'rgba(248,113,113,0.2)',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-400 text-base" />
              </div>
              <p className="text-red-400 text-sm flex-1">{error}</p>
              <button
                onClick={refetch}
                className="text-red-400 hover:text-red-300 text-xs underline cursor-pointer whitespace-nowrap"
              >
                Reintentar
              </button>
            </motion.div>
          )}

          {/* Stat cards row — Top Clientes | Citas hoy | Sin visitar */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
          >
            <TopCustomersWidget customers={topCustomers} loading={loading} />
            <StatCard
              label="Citas hoy"
              value={loading ? '—' : stats.today}
              icon={<Clock size={18} color={theme.colors.primary} />}
              accent
              index={1}
            />
            <DormantCustomersWidget customers={dormantCustomers} loading={loading} />
          </motion.div>

          {/* Quick info row — Horario de hoy | Estado de citas */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8"
          >
            {/* Today's schedule */}
            <motion.div
              variants={itemVariants}
              className="rounded-xl p-5 border"
              style={{
                backgroundColor: theme.colors.bgSurface,
                borderColor: theme.colors.border,
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h4
                  className="text-white text-sm font-semibold"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  Horario de hoy
                </h4>
                <div className="w-6 h-6 flex items-center justify-center">
                  <Timer size={14} color={theme.colors.primary} />
                </div>
              </div>

              {loading && (
                <div className="space-y-2.5 animate-pulse">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-3 bg-white/8 rounded flex-shrink-0" />
                      <div className="flex-1 h-8 bg-white/8 rounded-lg" />
                    </div>
                  ))}
                </div>
              )}

              {!loading && todayAppointments.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-sm" style={{ color: theme.colors.textDisabled }}>
                    Sin citas para hoy
                  </p>
                </div>
              )}

              {!loading && todayAppointments.length > 0 && (
                <div className="space-y-2.5">
                  {todayAppointments.map((appt, i) => (
                    <motion.div
                      key={appt.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.06 + 0.2 }}
                      className="flex items-center gap-3"
                    >
                      <span
                        className="text-xs font-mono w-10 flex-shrink-0"
                        style={{ color: theme.colors.textMuted }}
                      >
                        {appt.start_time?.slice(0, 5) ?? '—'}
                      </span>
                      <div
                        className="flex-1 rounded-lg px-3 py-1.5"
                        style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                      >
                        <p className="text-white/80 text-xs font-medium truncate">{appt.client_name}</p>
                        <p className="text-xs capitalize" style={{ color: theme.colors.textDisabled }}>
                          {appt.status}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Status breakdown */}
            <motion.div
              variants={itemVariants}
              className="rounded-xl p-5 border lg:col-span-2"
              style={{
                backgroundColor: theme.colors.bgSurface,
                borderColor: theme.colors.border,
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h4
                  className="text-white text-sm font-semibold"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  Estado de citas
                </h4>
                <div className="w-6 h-6 flex items-center justify-center">
                  <PieChart size={14} color={theme.colors.primary} />
                </div>
              </div>

              {loading ? (
                <div className="space-y-3 animate-pulse">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2 h-2 rounded-full bg-white/10" />
                        <div className="w-24 h-3 bg-white/8 rounded" />
                      </div>
                      <div className="w-8 h-4 bg-white/8 rounded" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {[
                    { label: 'Confirmadas', count: confirmed, color: '#D9D9D9', barColor: 'bg-[#D9D9D9]' },
                    { label: 'Pendientes', count: pending, color: '#888888', barColor: 'bg-[#888888]' },
                    { label: 'Canceladas', count: cancelled, color: '#f87171', barColor: 'bg-red-400' },
                  ].map((s, i) => {
                    const pct = stats.total > 0 ? Math.round((s.count / stats.total) * 100) : 0;
                    return (
                      <motion.div
                        key={s.label}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08 + 0.25 }}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2.5">
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: s.color }}
                            />
                            <span className="text-sm" style={{ color: theme.colors.textSecondary }}>
                              {s.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs" style={{ color: theme.colors.textMuted }}>
                              {pct}%
                            </span>
                            <span className="text-sm font-semibold" style={{ color: s.color }}>
                              {s.count}
                            </span>
                          </div>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                          <motion.div
                            className={`h-full ${s.barColor} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 0.7, delay: i * 0.1 + 0.3, ease: 'easeOut' }}
                          />
                        </div>
                      </motion.div>
                    );
                  })}
                  <div
                    className="pt-2 flex items-center justify-between"
                    style={{ borderTop: `1px solid ${theme.colors.border}` }}
                  >
                    <span className="text-xs" style={{ color: theme.colors.textMuted }}>
                      Total registros
                    </span>
                    <span className="text-sm font-semibold text-white">{stats.total}</span>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>

          {/* Recent appointments table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 }}
          >
            <RecentAppointments appointments={appointments} loading={loading} />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
